import React, { useState, useEffect } from 'react';
import { Location, ContractType, CONTRACT_TYPES } from '../types/location';

interface LocationFormModalProps {
  location?: Location;
  onSave: (data: Omit<Location, '_id' | 'createdAt'>) => void;
  onClose: () => void;
  existingLocations: Location[];
}

const LocationFormModal: React.FC<LocationFormModalProps> = ({
  location,
  onSave,
  onClose,
  existingLocations,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    shiftStaffCount: 1,
    contractType: 'Full-time' as ContractType,
    contactPerson: '',
    contactPhone: ''
  });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        address: location.address || '',
        shiftStaffCount: location.shiftStaffCount,
        contractType: location.contractType,
        contactPerson: location.contactPerson || '',
        contactPhone: location.contactPhone || ''
      });
    }
  }, [location]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate name
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    // Check for duplicate names
    const isDuplicate = existingLocations.some(
      loc => loc.name.toLowerCase() === formData.name.toLowerCase() && 
      (!location || loc._id !== location._id)
    );

    if (isDuplicate) {
      setError('A location with this name already exists');
      return;
    }

    // Validate staff count
    if (formData.shiftStaffCount < 1) {
      setError('Staff count must be at least 1');
      return;
    }

    // Validate contact person
    if (!formData.contactPerson.trim()) {
      setError('Contact person is required');
      return;
    }

    // Validate contact phone
    if (!formData.contactPhone.trim()) {
      setError('Contact phone is required');
      return;
    }

    // Create a new object with all required fields
    const locationData = {
      name: formData.name,
      address: formData.address,
      shiftStaffCount: formData.shiftStaffCount,
      contractType: formData.contractType,
      contactPerson: formData.contactPerson,
      contactPhone: formData.contactPhone
    };

    onSave(locationData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-lg w-full">
        <h2 className="text-xl font-bold mb-4">
          {location ? 'Edit Location' : 'Add New Location'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Location Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address (Optional)
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff per Shift
            </label>
            <input
              type="number"
              min="1"
              value={formData.shiftStaffCount}
              onChange={(e) => setFormData({ ...formData, shiftStaffCount: parseInt(e.target.value) })}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contract Type
            </label>
            <select
              value={formData.contractType}
              onChange={(e) => setFormData({ ...formData, contractType: e.target.value as ContractType })}
              className="w-full p-2 border rounded"
            >
              {CONTRACT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Contact Person Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Contact Phone Number"
            />
          </div>
          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}
          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationFormModal;
